// /start/:slug — the company's general sign-up link (docs/INTAKE-LINK-SPEC.md).
//
// One link a company can text to a new prospect, put on its website, or embed
// in a funnel (?embed=1). The visitor gives name / email / phone, becomes a lead
// or client on submit, can jump straight to the company's credit-monitoring
// enrollment (the company's OWN affiliate link), and continues into the secure
// intake (/intake/:token) to finish SSN, documents and monitoring login.
//
// Public page: no staff chrome, no auth. Every network call is a public route.
import { isClientMonitoringProvider } from "../lib/monitoring-policy";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { startApi, type StartPageInfo, type StartSubmitResult } from "../lib/api";

type Provider = "my_free_score_now" | "smart_credit";
const PROVIDER_LABEL: Record<Provider, string> = { my_free_score_now: "MyFreeScoreNow", smart_credit: "SmartCredit" };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const digits = (v: string) => v.replace(/\D/g, "");
// Attribution comes from the URL, which anyone can craft. Strip control
// characters so a hostile link cannot turn into a backend validation error the
// visitor has no field to fix.
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g;
const clean = (v: string, max: number) => v.replace(CONTROL_CHARS, " ").replace(/\s+/g, " ").trim().slice(0, max);

type Snapshot = { first: string; email: string; choice: Provider | null };

export default function StartIntakePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  const [info, setInfo] = useState<StartPageInfo | null>(null);
  const [loadError, setLoadError] = useState<"not_found" | "network" | null>(null);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [choice, setChoice] = useState<Provider | null>(null);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");   // honeypot — real people never see it
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<{ result: StartSubmitResult; snap: Snapshot } | null>(null);
  const redirectTimer = useRef<number | null>(null);
  const submitSeq = useRef(0);               // a response for an older slug/submission is ignored
  const firstInvalidRef = useRef<HTMLInputElement | null>(null);
  const consentRef = useRef<HTMLInputElement | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);

  // "src=facebook utm_source=fb utm_campaign=sept" — src first, then every utm_*
  // in URL order, cut at the contract's 80 characters (the leading parts survive).
  const source = useMemo(() => {
    const parts: string[] = [];
    const src = params.get("src");
    if (src) parts.push(`src=${src}`);
    params.forEach((v, k) => { if (/^utm_/i.test(k) && v) parts.push(`${k}=${v}`); });
    const joined = clean(parts.join(" "), 80);
    return joined || null;
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    // A different company's link (back/forward between funnels) must not show
    // the previous company's branding, form, consent, choice, result or redirect.
    submitSeq.current++;
    if (redirectTimer.current) { window.clearTimeout(redirectTimer.current); redirectTimer.current = null; }
    setInfo(null); setLoadError(null); setDone(null); setChoice(null); setErrors({}); setSubmitError(null);
    setFirst(""); setLast(""); setEmail(""); setPhone(""); setConsent(false); setSubmitting(false);
    startApi.get(slug)
      .then((r) => { if (!cancelled) setInfo(r); })
      .catch((e: any) => { if (!cancelled) setLoadError(e?.status === 404 ? "not_found" : "network"); });
    return () => { cancelled = true; submitSeq.current++; if (redirectTimer.current) window.clearTimeout(redirectTimer.current); };
  }, [slug]);

  useEffect(() => { if (done) resultRef.current?.focus(); }, [done]);

  const offers: Provider[] = useMemo(() => {
    if (!info) return [];
    return (["my_free_score_now", "smart_credit"] as Provider[]).filter((p) => isClientMonitoringProvider(p) && !!info.monitoring[p]);
  }, [info]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!first.trim() || first.trim().length > 80) e.first_name = "Enter your first name.";
    if (!last.trim() || last.trim().length > 80) e.last_name = "Enter your last name.";
    if (!EMAIL_RE.test(email.trim()) || email.trim().length > 254) e.email = "Enter a valid email address.";
    const d = digits(phone);
    if (phone.trim() && (d.length < 7 || d.length > 20)) e.phone = "Enter a valid phone number.";
    if (!consent) e.consent = "Please agree so we can contact you.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) { window.setTimeout(() => firstInvalidRef.current?.focus(), 0); return; }
    setSubmitting(true); setSubmitError(null);
    const seq = ++submitSeq.current;
    // What the person actually sent — the success screen must not read live
    // state that could change under it.
    const snap: Snapshot = { first: first.trim(), email: email.trim().toLowerCase(), choice };
    try {
      const r = await startApi.submit(slug, {
        first_name: snap.first, last_name: last.trim(), email: snap.email,
        phone: phone.trim() ? phone.trim() : null,
        monitoring_choice: snap.choice, consent: true, source,
        referrer: document.referrer ? clean(document.referrer, 500) : null,
        website,
      });
      if (seq !== submitSeq.current) return;          // slug changed mid-flight
      setDone({ result: r, snap });
      // No enrollment step to show → go straight into the secure intake. Never
      // from inside an embed: the intake page refuses to be framed, so there the
      // person gets a link that opens it in a new tab instead.
      if (r.intake_url && !(snap.choice && r.monitoring_url) && !embed) {
        redirectTimer.current = window.setTimeout(() => { window.location.assign(r.intake_url!); }, 1500);
      }
    } catch (e: any) {
      if (seq !== submitSeq.current) return;
      if (e?.status === 400 && Array.isArray(e?.body?.fields)) {
        const visible = new Set(["first_name", "last_name", "email", "phone", "consent"]);
        const fe: Record<string, string> = {};
        const hidden: string[] = [];
        for (const f of e.body.fields as string[]) {
          const key = String(f).split(/[ :(]/)[0];
          if (visible.has(key)) fe[key] = "Please check this field."; else hidden.push(key);
        }
        setErrors(fe);
        setSubmitError(hidden.length && !Object.keys(fe).length
          ? "Something in the link you used isn't valid. Please open the sign-up page directly and try again."
          : "Please check the highlighted fields.");
        window.setTimeout(() => firstInvalidRef.current?.focus(), 0);
      } else if (e?.status === 429) {
        setSubmitError("Too many attempts right now — please try again in a few minutes.");
      } else {
        setSubmitError(e?.message || "Something went wrong. Please try again.");
      }
    } finally {
      if (seq === submitSeq.current) setSubmitting(false);
    }
  }

  const company = info?.company.name || "our team";
  const contact = info?.company.support_email || info?.company.phone || null;

  if (loadError) {
    return (
      <Frame embed={embed}>
        <h1 style={{ marginTop: 0 }}>{loadError === "not_found" ? "This sign-up link isn't active" : "We couldn't load this page"}</h1>
        <p className="muted">
          {loadError === "not_found"
            ? "Please check the link you were sent, or contact the company that gave it to you."
            : "Please check your connection and reload."}
        </p>
      </Frame>
    );
  }
  if (!info) return <Frame embed={embed}><p className="muted">Loading…</p></Frame>;

  if (done) {
    const { result, snap } = done;
    const showEnroll = !!(snap.choice && result.monitoring_url);
    const enroll = showEnroll && (
      <a className="primary-button" href={result.monitoring_url!} target="_blank" rel="noopener noreferrer">
        Open {PROVIDER_LABEL[snap.choice!]} enrollment
      </a>
    );
    return (
      <Frame embed={embed}>
        {!embed && <Brand info={info} />}
        <div ref={resultRef} tabIndex={-1} role="status" aria-live="polite" style={{ outline: 0 }}>
          <h1 style={{ marginTop: 0 }}>Thanks, {snap.first} — you're in.</h1>
          {result.client_state === "existing" ? (
            result.delivery === "sent" ? (
              <>
                <p className="muted">
                  We already have you on file, so we've emailed your secure intake link to <strong>{maskEmail(snap.email)}</strong>.
                  Check your inbox (and spam) — the link is good for 14 days.
                </p>
                {showEnroll && <p className="muted">Meanwhile you can create your {PROVIDER_LABEL[snap.choice!]} account through {company}'s link: {enroll}</p>}
              </>
            ) : (
              <>
                <p className="muted">
                  We already have you on file, but we couldn't email your secure link just now.
                  {contact ? <> Please contact {company} at <strong>{contact}</strong> and they'll send it to you.</> : <> Please contact {company} and they'll send it to you.</>}
                </p>
                {showEnroll && <p className="muted">You can still create your {PROVIDER_LABEL[snap.choice!]} account through {company}'s link: {enroll}</p>}
              </>
            )
          ) : showEnroll ? (
            <>
              <p className="muted">
                Step 1: create your <strong>{PROVIDER_LABEL[snap.choice!]}</strong> account through {company}'s link
                (it opens in a new tab). Step 2: come back here and continue to your secure intake.
              </p>
              <div className="form-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {enroll}
                {result.intake_url && (
                  <a className="btn-secondary" href={result.intake_url} {...(embed ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                    Continue to your secure intake
                  </a>
                )}
              </div>
            </>
          ) : result.intake_url ? (
            embed
              ? <p className="muted"><a className="primary-button" href={result.intake_url} target="_blank" rel="noopener noreferrer">Continue to your secure intake</a></p>
              : <p className="muted">Taking you to your secure intake… <a href={result.intake_url}>Continue now</a>.</p>
          ) : (
            <p className="muted">We've received your details. {company} will be in touch shortly.</p>
          )}
        </div>
      </Frame>
    );
  }

  // First invalid field (in visual order) gets focus after a failed submit.
  const order = ["first_name", "last_name", "email", "phone", "consent"] as const;
  const firstInvalid = order.find((k) => errors[k]);
  const ref = (k: string) => (k === firstInvalid ? firstInvalidRef : undefined);
  const consentIsFirst = firstInvalid === "consent";

  return (
    <Frame embed={embed}>
      {!embed && <Brand info={info} />}
      <h1 style={{ marginTop: 0 }}>Get started with {company}</h1>
      <p className="muted small" style={{ marginTop: 4 }}>
        Tell us who you are and we'll set up your secure intake. Takes about a minute.
      </p>
      <form className="stacked-form" onSubmit={submit} noValidate aria-busy={submitting}>
        <fieldset disabled={submitting} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div className="start-name-row">
            <Field id="start-first" label="First name" error={errors.first_name}>
              <input id="start-first" ref={ref("first_name")} value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" maxLength={80} required
                aria-invalid={!!errors.first_name} aria-describedby={errors.first_name ? "start-first-err" : undefined} />
            </Field>
            <Field id="start-last" label="Last name" error={errors.last_name}>
              <input id="start-last" ref={ref("last_name")} value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" maxLength={80} required
                aria-invalid={!!errors.last_name} aria-describedby={errors.last_name ? "start-last-err" : undefined} />
            </Field>
          </div>
          <Field id="start-email" label="Email" error={errors.email}>
            <input id="start-email" ref={ref("email")} type="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" maxLength={254} required
              aria-invalid={!!errors.email} aria-describedby={errors.email ? "start-email-err" : undefined} />
          </Field>
          <Field id="start-phone" label="Mobile phone (optional)" error={errors.phone}>
            <input id="start-phone" ref={ref("phone")} type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" maxLength={20}
              aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "start-phone-err" : undefined} />
          </Field>

          {offers.length > 0 && (
            <fieldset className="start-choice" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="small" style={{ fontWeight: 600, marginBottom: 6 }}>
                Credit monitoring (optional) — pick one and we'll send you to enroll after this step
              </legend>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {offers.map((p) => (
                  <label key={p} className={`btn-secondary ${choice === p ? "start-choice-on" : ""}`} style={{ cursor: "pointer" }}>
                    <input type="radio" name="monitoring_choice" value={p} checked={choice === p}
                      onChange={() => setChoice(p)} style={{ marginRight: 6 }} />
                    {PROVIDER_LABEL[p]}
                  </label>
                ))}
                {choice && (
                  <button type="button" className="btn-link small" onClick={() => setChoice(null)}>Skip for now</button>
                )}
              </div>
            </fieldset>
          )}

          {/* Honeypot: hidden from people, filled by bots. Not display:none so
              naive bots that skip hidden inputs still fill it. */}
          <div aria-hidden="true" style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}>
            <label>Website <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
          </div>

          <label className="small" htmlFor="start-consent" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <input id="start-consent" ref={consentIsFirst ? firstInvalidRef : consentRef} type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 3 }}
              aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "start-consent-err" : undefined} />
            <span>I agree that {company} may contact me by email, text or phone about my request.</span>
          </label>
          {errors.consent && <p id="start-consent-err" className="portal-error small" role="alert">{errors.consent}</p>}
          {submitError && <p className="portal-error" role="alert">{submitError}</p>}

          <div className="form-actions">
            <button type="submit" className="primary-button wide-button" disabled={submitting}>
              {submitting ? "Sending…" : "Get started"}
            </button>
          </div>
        </fieldset>
      </form>
      {!embed && contact && (
        <p className="muted small intake-footer">
          Questions? {info.company.support_email && <a href={`mailto:${info.company.support_email}`}>{info.company.support_email}</a>}
          {info.company.support_email && info.company.phone ? " · " : ""}{info.company.phone}
        </p>
      )}
    </Frame>
  );
}

// Module-scope so React keeps the same component type across renders — a
// wrapper declared inside the page remounts the form (and drops input focus)
// on every keystroke.
function Frame({ embed, children }: { embed: boolean; children: React.ReactNode }) {
  return embed
    ? <div className="intake-card start-embed">{children}</div>
    : <div className="intake-page"><div className="intake-card">{children}</div></div>;
}

function Brand({ info }: { info: StartPageInfo }) {
  return (
    <div className="intake-header" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
      {info.company.logo_url && <img src={info.company.logo_url} alt="" style={{ height: 40, maxWidth: 160, width: "auto", objectFit: "contain" }} />}
      <strong>{info.company.name}</strong>
    </div>
  );
}

/** Same structure as every other form in the app (.form-field), with the label
 *  bound to the control and the error announced against it. */
function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="form-field">
      <label htmlFor={id}><span>{label}</span></label>
      {children}
      {error && <p id={`${id}-err`} className="portal-error small" role="alert" style={{ margin: "4px 0 0" }}>{error}</p>}
    </div>
  );
}

/** r•••@example.com — enough for the person to recognise it, nothing for a stranger. */
function maskEmail(v: string): string {
  const [user, domain] = v.trim().toLowerCase().split("@");
  if (!user || !domain) return "your email";
  return `${user[0]}•••@${domain}`;
}
